import React from 'react';

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Tooltip,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';

import {
  childApplyListSelector,
  applyNurseryListSelector,
  isInitApplyNurseryListSelector,
} from '../../selector';
import {
  getApplyNurseryList,
  sendChildApply,
} from '../../thunk';

import Confirm from './Confirm';

import type { UserAttributes } from '@/infra/cognitoIApi';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { ChildApplyJson } from '@/types/domain/childApply/entities/childApplyEntity';
import type { FacilityJson } from '@/types/domain/childApply/entities/facilityEntity';
import type { ApplyNurseryType } from '@/types/domain/childApply/redux';

import DialogTitle from '@/components/DialogTitle';
import DialogTransition from '@/components/DialogTransition';
import ChildInfo from '@/domain/childApply/component/ChildInfo';
import { uiSlice } from '@/domain/ui/redux';

const NG_SEARCH_WORDS = new Set([
  '保',
  '育',
  '園',
  '保育',
  '育園',
  '保育園',
  'こ',
  'ど',
  'も',
  'こど',
  'ども',
  'も園',
  'こども',
  'ども園',
  'こども園',
]);

/**
 * プロパティ
 */
export type Props = {
  /** 子ども */
  child: ChildJson,
  /** ユーザー名 */
  username: string,
  /** 属性 */
  attrs: UserAttributes,
  /** 施設リスト */
  facilityList: FacilityJson[],
}

const Apply: React.FC<Props> = ({
  child, username, attrs, facilityList,
}) => {
  const childApplyList = useSelector(childApplyListSelector);
  const nurseryList = useSelector(applyNurseryListSelector);
  const isInitApplyNurseryList = useSelector(isInitApplyNurseryListSelector);
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = React.useState(false);
  const [confirmData, setConfirmData] = React.useState<ChildApplyJson | undefined>(undefined);
  const [searchWord, setSearchWord] = React.useState('');
  const isLoadingNurseryList = isOpen && !isInitApplyNurseryList;
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<{ facilityId: string }>({
    defaultValues: {
      facilityId: '',
    },
  });
  React.useEffect(() => {
    if (isOpen && !isInitApplyNurseryList) {
      dispatch(getApplyNurseryList());
    }
  }, [dispatch, isOpen, isInitApplyNurseryList]);
  React.useEffect(() => {
    if (isOpen) {
      setValue('facilityId', '');
      setSearchWord('');
    }
  }, [isOpen, setValue]);

  const normalizedSearchWord = React.useMemo(
    () => searchWord.trim(),
    [searchWord],
  );
  const isSearchEnabled = (
    normalizedSearchWord.length > 0
    && !NG_SEARCH_WORDS.has(normalizedSearchWord)
  );
  const isNgWord = normalizedSearchWord.length > 0 && NG_SEARCH_WORDS.has(normalizedSearchWord);
  const filteredNurseryList = React.useMemo(
    () => (isSearchEnabled
      ? nurseryList.filter((nursery) => nursery.name.includes(normalizedSearchWord))
      : []),
    [isSearchEnabled, nurseryList, normalizedSearchWord],
  );
  const noOptionsText = React.useMemo(() => {
    if (isNgWord) {
      return 'このキーワードでは検索できません';
    }
    if (normalizedSearchWord.length === 0) {
      return '1文字以上入力してください';
    }
    return '該当する保育園がありません';
  }, [isNgWord, normalizedSearchWord.length]);

  const onApply = (input: ChildApplyJson | undefined) => {
    if (input) {
      dispatch(sendChildApply(input));
      setConfirmData(undefined);
      setIsOpen(false);
    } else {
      setConfirmData(undefined);
    }
  };
  const onSubmit = async (input: { facilityId: string }) => {
    const nursery = nurseryList.find((v) => v.id === input.facilityId);
    if (!nursery) {
      dispatch(uiSlice.actions.setMessage({ key: 'warning', content: '施設を選択してください。' }));
      return;
    }
    const facility = facilityList.find((v) => v.data.facilityId === input.facilityId);
    if (facility) {
      dispatch(uiSlice.actions.setMessage({ key: 'warning', content: 'すでに所属済みです' }));
    } else {
      const apply = childApplyList.find((v) => v.data.childId === child.childId && v.pub?.data.applyStatus === '申請中');
      if (apply) {
        dispatch(uiSlice.actions.setMessage({ key: 'warning', content: 'すでに申請済みです' }));
      } else {
        const parentChildId = `${child.parentId}_${child.childId}`;
        const jsonApply: ChildApplyJson = {
          data: {
            facilityId: input.facilityId,
            facilityName: nursery.name,
            childId: child.childId,
            childName: child.name,
            childFamilyName: child.familyName,
            childNameKana: child.nameKana,
            childFamilyNameKana: child.familyNameKana,
            gender: child.gender,
            birthday: child.birthday,
            email: attrs.email,
            parentId: username,
            parentName: attrs.name,
            parentFamilyName: attrs.familyName,
            parentNameKana: attrs.nameKana,
            parentFamilyNameKana: attrs.familyNameKana,
            postalCode: attrs.postalCode,
            address: attrs.address,
            tel: attrs.tel,
            applyDateTime: (new Date()).toJSON(),
          },
          pub: {
            sendId: parentChildId,
            replyId: input.facilityId,
            data: {
              applyStatus: '申請中',
            },
          },
        };
        setConfirmData(jsonApply);
      }
    }
  };
  return (
    <div>
      <Tooltip title="申請を行います">
        <span>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setIsOpen(true);
            }}
            style={{ marginBottom: '5px' }}
          >
            登録申請
          </Button>
        </span>
      </Tooltip>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        fullScreen
        TransitionComponent={DialogTransition}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle title="登録申請" />
          <DialogContent>
            <p>保育園名を1文字以上入力して候補から選択し、「申請内容を確認する」ボタンを押してください。</p>
            <ChildInfo data={child} />
            <div style={{ margin: '10px 0' }}>
              <Controller
                name="facilityId"
                control={control}
                rules={{
                  required: '必須項目です',
                }}
                render={({ field }) => (
                  <Autocomplete
                    options={filteredNurseryList}
                    getOptionLabel={(option: ApplyNurseryType) => option.name}
                    isOptionEqualToValue={(option: ApplyNurseryType, value: ApplyNurseryType) => (
                      option.id === value.id
                    )}
                    filterOptions={(options) => options}
                    value={nurseryList.find((nursery) => nursery.id === field.value) || null}
                    inputValue={searchWord}
                    onInputChange={(_, value, reason) => {
                      setSearchWord(value);
                      if (reason === 'input' || reason === 'clear') {
                        field.onChange('');
                      }
                    }}
                    onChange={(_, selected) => {
                      field.onChange(selected?.id || '');
                    }}
                    disabled={isLoadingNurseryList || nurseryList.length === 0}
                    noOptionsText={noOptionsText}
                    renderInput={(params) => (
                      <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        label="保育園名"
                        error={errors.facilityId !== undefined}
                        helperText={errors.facilityId?.message || ''}
                      />
                    )}
                  />
                )}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={isLoadingNurseryList || nurseryList.length === 0}
            >
              申請内容を確認する
            </Button>
            <Button variant="contained" onClick={() => setIsOpen(false)} color="primary">
              閉じる
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Confirm data={confirmData} onApply={onApply} />
    </div>
  );
};

export default Apply;
